import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { getVideoStreamUrl } from '@/components/ui/PhotoGallery';
import { toast } from 'sonner';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { ArrowLeft, Download, Share2, Award, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Base URL du gateway pour les URLs d'images (auth via cookie de session)
const GATEWAY_BASE_URL = import.meta.env.VITE_GATEWAY_BASE_URL ?? '/api/gateway';

// Transformer une URL /api/... en URL gateway
const toGatewayUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) {
        return GATEWAY_BASE_URL + url.substring(4);
    }
    return url;
};

const formatDateTime = (value) => {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString('fr-FR');
    } catch {
        return '—';
    }
};

const formatDate = (value) => {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleDateString('fr-FR');
    } catch {
        return '—';
    }
};

const formatTime = (value) => {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '—';
    }
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toFixed(4);
};

export default function MediaDetail({ type = 'picture' }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { pictureId } = useParams();
    const { getCache, setCache } = useApiCache();
    const { can } = usePermissions();

    // URL source pour la navigation retour (si navigué depuis un rapport de procédure)
    const sourceUrl = location.state?.from;

    // État pour stocker le rootFolderId et l'interventionId résolus depuis l'API
    const [resolvedIds, setResolvedIds] = useState({
        rootFolderId: null,
        interventionId: null,
        isFolderGallery: false,
        isResolved: false,
    });

    const [isLoading, setIsLoading] = useState(true);

    const [picture, setPicture] = useState(null);
    const [pictureStatuses, setPictureStatuses] = useState([]);
    const [certificate, setCertificate] = useState(null);
    const [certificateStatuses, setCertificateStatuses] = useState([]);

    const [previewUrl, setPreviewUrl] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
    const [isSharing, setIsSharing] = useState(false);

    const [originalImageUrl, setOriginalImageUrl] = useState('');
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const lightboxRef = useRef(null);

    const certificateId = useMemo(() => {
        return (
            picture?.certificate_uniq_id ??
            picture?.certificate_id ??
            picture?.certificate?.id ??
            picture?.certificate?.uniq_id ??
            null
        );
    }, [picture]);

    // Lien de vérification fourni par l'API (le même que dans le PDF du certificat), sans le protocole
    const certificateDisplayUrl = (certificate?.url ?? '').replace(/^https?:\/\//, '');

    // Résoudre les IDs (rootFolderId et interventionId) via l'API getPicture
    useEffect(() => {
        if (!pictureId) {
            setResolvedIds({ rootFolderId: null, interventionId: null, isFolderGallery: false, isResolved: true });
            return;
        }

        // Vérifier le cache d'abord
        const cacheKey = `picture-ids-${pictureId}`;
        const cached = getCache(cacheKey);
        if (cached) {
            setResolvedIds(cached);
            return;
        }

        let isMounted = true;

        (async () => {
            try {
                // Récupérer les détails de la photo pour obtenir folder_uniq_id et intervention_uniq_id
                const pictureRes = await horodatyApi.getPicture(pictureId, { details: true });
                const pictureData = pictureRes?.data?.data;

                if (!isMounted) return;

                if (pictureData?.folder_uniq_id && pictureData?.intervention_uniq_id) {
                    const isFolderGallery = pictureData?.intervention_type === 'folder_gallery';
                    const resolved = {
                        rootFolderId: pictureData.folder_uniq_id,
                        interventionId: pictureData.intervention_uniq_id,
                        isFolderGallery,
                        isResolved: true,
                    };
                    setResolvedIds(resolved);
                    setCache(cacheKey, resolved, 10 * 60 * 1000); // 10 min TTL
                } else {
                    console.warn('[PictureDetail] Could not resolve IDs for picture:', pictureId);
                    setResolvedIds({
                        rootFolderId: null,
                        interventionId: null,
                        isFolderGallery: false,
                        isResolved: true,
                    });
                }
            } catch (error) {
                console.error('[PictureDetail] Error resolving IDs:', error);
                if (!isMounted) return;
                setResolvedIds({ rootFolderId: null, interventionId: null, isFolderGallery: false, isResolved: true });
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [pictureId, getCache, setCache]);

    // Utiliser les IDs résolus pour les appels API
    const folderId = resolvedIds.rootFolderId;
    const interventionId = resolvedIds.interventionId;

    // Navigation retour personnalisée vers le dossier contenant la photo
    const goBack = useCallback(() => {
        // Si on vient d'une page spécifique (ex: rapport de procédure), y retourner
        if (sourceUrl) {
            navigate(sourceUrl, { replace: true });
            return;
        }
        // Si la photo est dans une galerie (folder_gallery), retourner au dossier parent
        if (resolvedIds.isFolderGallery && folderId) {
            navigate(`/folders/${folderId}`, { replace: true });
        } else if (interventionId) {
            navigate(`/folders/${interventionId}`, { replace: true });
        } else if (folderId) {
            navigate(`/folders/${folderId}`, { replace: true });
        } else {
            navigate('/folders', { replace: true });
        }
    }, [navigate, sourceUrl, interventionId, folderId, resolvedIds.isFolderGallery]);

    useEffect(() => {
        // Attendre que les IDs soient résolus
        if (!resolvedIds.isResolved) return;

        let isMounted = true;

        const load = async () => {
            if (!folderId || !interventionId || !pictureId) {
                setIsLoading(false);
                return;
            }

            // Clés de cache pour les métadonnées uniquement
            const detailsCacheKey = `picture-details-${pictureId}`;
            const statusCacheKey = `picture-status-${pictureId}`;

            // Vérifier le cache pour les détails et statuts
            const cachedDetails = getCache(detailsCacheKey);
            const cachedStatus = getCache(statusCacheKey);

            // Si les métadonnées sont en cache, utiliser le cache
            // Les images utilisent les URLs gateway (cache HTTP natif du navigateur)
            if (cachedDetails && cachedStatus != null) {
                setPicture(cachedDetails);
                setPictureStatuses(cachedStatus || []);
                // URLs gateway pour les images (auth via cookie de session)
                const fullscreenUrl = toGatewayUrl(`/api/pictures/${pictureId}?format=fullscreen&band=false`);
                const originalUrl = toGatewayUrl(`/api/pictures/${pictureId}?format=original&band=false`);
                setPreviewUrl(fullscreenUrl);
                setOriginalImageUrl(originalUrl);

                // Précharger pour obtenir les dimensions
                const img = new Image();
                img.onload = () => {
                    if (isMounted) {
                        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                    }
                };
                img.src = originalUrl;

                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            try {
                // Load details and status first (these are critical)
                const [detailsRes, statusRes] = await Promise.all([
                    cachedDetails
                        ? Promise.resolve({ data: { data: cachedDetails } })
                        : horodatyApi.getInterventionPicture(folderId, interventionId, pictureId, { details: true }),
                    cachedStatus != null
                        ? Promise.resolve({ data: { data: cachedStatus } })
                        : horodatyApi
                              .getInterventionPictureStatus(folderId, interventionId, pictureId)
                              .catch(() => null),
                ]);

                if (!isMounted) return;

                const doc = detailsRes?.data?.data ?? detailsRes?.data ?? null;
                setPicture(doc);
                if (doc && !cachedDetails) {
                    setCache(detailsCacheKey, doc, 10 * 60 * 1000); // 10 min TTL
                }

                const statuses = statusRes?.data?.data ?? [];
                const statusArray = Array.isArray(statuses) ? statuses : [];
                setPictureStatuses(statusArray);
                if (!cachedStatus) {
                    setCache(statusCacheKey, statusArray, 10 * 60 * 1000);
                }

                // Utiliser directement les URLs au lieu de télécharger les blobs
                // Le navigateur gère le cache HTTP nativement, plus performant
                const fullscreenUrl = toGatewayUrl(`/api/pictures/${pictureId}?format=fullscreen&band=false`);
                const originalUrl = toGatewayUrl(`/api/pictures/${pictureId}?format=original&band=false`);

                setPreviewUrl(fullscreenUrl);
                setOriginalImageUrl(originalUrl);

                // Précharger l'image pour obtenir les dimensions (pour PhotoSwipe)
                const img = new Image();
                img.onload = () => {
                    if (isMounted) {
                        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                    }
                };
                img.src = originalUrl;
            } catch (error) {
                const message = error?.response?.data?.message || t('folders:impossibleDeChargerLesDetailsDeLaPhoto');
                if (!isMounted) return;
                toast.error(message);
                setPicture(null);
                setPictureStatuses([]);
            } finally {
                if (!isMounted) return;
                setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, [folderId, interventionId, pictureId, resolvedIds.isResolved, getCache, setCache]);

    useEffect(() => {
        let isMounted = true;

        const loadCertificate = async () => {
            if (!folderId || !interventionId || !pictureId || !certificateId) return;

            // Clés de cache
            const certCacheKey = `certificate-${certificateId}`;
            const certStatusCacheKey = `certificate-status-${certificateId}`;

            // Vérifier le cache
            const cachedCert = getCache(certCacheKey);
            const cachedCertStatus = getCache(certStatusCacheKey);

            if (cachedCert != null && cachedCertStatus != null) {
                setCertificate(cachedCert);
                setCertificateStatuses(cachedCertStatus || []);
                return;
            }

            try {
                const [certRes, certStatusRes] = await Promise.all([
                    cachedCert != null
                        ? Promise.resolve({ data: { data: cachedCert } })
                        : horodatyApi.getInterventionPictureCertificate(
                              folderId,
                              interventionId,
                              pictureId,
                              certificateId,
                              {
                                  details: true,
                              },
                          ),
                    cachedCertStatus != null
                        ? Promise.resolve({ data: { data: cachedCertStatus } })
                        : horodatyApi.getInterventionPictureCertificateStatus(
                              folderId,
                              interventionId,
                              pictureId,
                              certificateId,
                          ),
                ]);

                if (!isMounted) return;

                const cert = certRes?.data?.data ?? null;
                setCertificate(cert);
                if (cachedCert == null) {
                    setCache(certCacheKey, cert, 10 * 60 * 1000);
                }

                const statuses = certStatusRes?.data?.data ?? [];
                const statusArray = Array.isArray(statuses) ? statuses : [];
                setCertificateStatuses(statusArray);
                if (cachedCertStatus == null) {
                    setCache(certStatusCacheKey, statusArray, 10 * 60 * 1000);
                }
            } catch {
                if (!isMounted) return;
                setCertificate(null);
                setCertificateStatuses([]);
            }
        };

        loadCertificate();

        return () => {
            isMounted = false;
        };
    }, [folderId, interventionId, pictureId, certificateId, getCache, setCache]);

    // Cleanup lightbox on unmount
    useEffect(() => {
        return () => {
            if (lightboxRef.current) {
                lightboxRef.current.destroy();
                lightboxRef.current = null;
            }
        };
    }, []);

    const handleDownloadCertificate = async () => {
        if (!folderId || !interventionId || !pictureId || !certificateId) return;

        setIsDownloadingCertificate(true);

        try {
            const res = await horodatyApi.getInterventionPictureCertificate(
                folderId,
                interventionId,
                pictureId,
                certificateId,
            );
            const blob = res?.data;
            if (!blob) return;

            const url = URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = certificate?.file_name || 'certificat.pdf';
            window.document.body.appendChild(a);
            a.click();
            a.remove();

            setTimeout(() => {
                try {
                    URL.revokeObjectURL(url);
                } catch {
                    // ignore
                }
            }, 500);
        } catch {
            toast.error(t('folders:impossibleDeTelechargerLeCertificat'));
        } finally {
            setIsDownloadingCertificate(false);
        }
    };

    const downloadBlob = (blob, fileName) => {
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = fileName || 'download';
        window.document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        }, 500);
    };

    const handleDownloadImage = async () => {
        if (!folderId || !interventionId || !pictureId) return;

        setIsDownloading(true);

        try {
            // Pour les vidéos, télécharger le MP4 brut via l'endpoint streaming ;
            // pour les photos, l'endpoint image classique.
            const blob = isVideoMedia
                ? await fetch(getVideoStreamUrl(pictureId), { credentials: 'include' }).then((r) => {
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      return r.blob();
                  })
                : (
                      await horodatyApi.getInterventionPicture(folderId, interventionId, pictureId, {
                          format: 'original',
                          band: false,
                      })
                  )?.data;

            if (!blob || blob.size === 0) {
                toast.error(t('folders:leFichierEstIndisponible'));
                return;
            }

            // Check if the blob is actually a JSON error response
            if (blob.type === 'application/json') {
                const text = await blob.text();
                try {
                    const error = JSON.parse(text);
                    toast.error(error?.message || t('folders:leFichierEstIndisponible'));
                } catch {
                    toast.error(t('folders:leFichierEstIndisponible'));
                }
                return;
            }

            const fallbackName = isVideoMedia ? 'video.mp4' : 'photo.jpg';
            downloadBlob(blob, picture?.file_name || fallbackName);
        } catch (error) {
            // Handle blob error response
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    toast.error(errorData?.message || t('folders:impossibleDeTelechargerLeFichier'));
                } catch {
                    toast.error(t('folders:impossibleDeTelechargerLeFichier'));
                }
            } else {
                toast.error(error?.response?.data?.message || t('folders:impossibleDeTelechargerLeFichier'));
            }
        } finally {
            setIsDownloading(false);
        }
    };

    const handleShare = async () => {
        if (!folderId || !interventionId || !pictureId) return;

        if (!('share' in navigator)) {
            toast.error(t('folders:partageIndisponibleSurCeNavigateur'));
            return;
        }

        setIsSharing(true);

        try {
            const res = await horodatyApi.getInterventionPicture(folderId, interventionId, pictureId, {
                format: 'original',
                band: false,
            });

            const blob = res?.data;
            if (!blob) return;

            const fileName = picture?.file_name || 'photo.jpg';
            const fileType = picture?.mime || blob.type || 'image/jpeg';
            const file = new File([blob], fileName, { type: fileType });

            // Some browsers require canShare for files.
            if (navigator.canShare && !navigator.canShare({ files: [file] })) {
                toast.error(t('folders:partageIndisponiblePourCeFichier'));
                return;
            }

            await navigator.share({
                title: docName,
                files: [file],
            });
        } catch {
            // user canceled or share failed
        } finally {
            setIsSharing(false);
        }
    };

    const isVideoMedia = typeof picture?.mime === 'string' && picture.mime.startsWith('video/');
    const docName = picture?.name || picture?.file_name || (isVideoMedia ? t('folders:video') : t('folders:photo'));

    const handleImageClick = useCallback(() => {
        const imageUrl = originalImageUrl || previewUrl;
        if (!imageUrl) return;

        // Use actual dimensions or fallback to screen size
        const width = imageDimensions.width || window.innerWidth;
        const height = imageDimensions.height || window.innerHeight;

        const lightbox = new PhotoSwipeLightbox({
            dataSource: [
                {
                    src: imageUrl,
                    width,
                    height,
                    alt: docName,
                },
            ],
            pswpModule: () => import('photoswipe'),
            showHideAnimationType: 'zoom',
            bgOpacity: 1,
        });

        lightbox.init();
        lightbox.loadAndOpen(0);
        lightboxRef.current = lightbox;
    }, [originalImageUrl, previewUrl, imageDimensions, docName]);

    const formatFileSize = (bytes) => {
        if (!bytes) return '—';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(1)} MB`;
    };

    const formatDuration = (durationMs) => {
        if (!durationMs || durationMs <= 0) return '—';
        const totalSeconds = Math.round(durationMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes === 0) return `${seconds}s`;
        return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    };

    const formatDimensions = ({ width, height }) => {
        if (!width || !height) return '—';
        return `${width} × ${height} px`;
    };

    const getOrientation = () => {
        // TODO: Get from metadata
        return 'Paysage';
    };

    const getQuality = () => {
        // TODO: Get from metadata
        return 'Haute';
    };

    // Try to get horodatage date from created_at or status_history
    const horodatageTimestamp =
        picture?.created_at ?? picture?.status?.created_at ?? picture?.status_history?.[0]?.created_at ?? null;
    const horodatageDate = horodatageTimestamp ? formatDate(horodatageTimestamp) : '—';
    const horodatageTime = horodatageTimestamp ? formatTime(horodatageTimestamp) : '—';

    return (
        <div className="pb-[120px] md:pb-0">
            {/* Header */}
            <div className="position-relative">
                <div className="bg-card px-[20px] py-[18px] flex items-center gap-[12px] border-b border-border shrink-0">
                    <Button variant="plain" color="neutral" size="auto" onClick={goBack} className="h-9 w-9 shrink-0">
                        <ArrowLeft className="h-6 w-6 text-foreground" />
                    </Button>
                    <h1 className="text-[18px] text-foreground flex-1">
                        {isVideoMedia ? t('folders:detailDeLaVideo') : t('folders:detailDeLaPhoto')}
                    </h1>
                    <Button
                        variant="plain"
                        color="neutral"
                        size="auto"
                        onClick={handleDownloadImage}
                        disabled={isLoading || isDownloading}
                        className="h-[36px] w-[36px] rounded-[10px] bg-muted hover:bg-border shrink-0"
                        aria-label={t('folders:telecharger')}
                    >
                        <Download className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                    </Button>
                    {/* #TOIMPLEMENT share_picture_api
                    <Button
                        variant="plain" color="neutral" size="auto"
                        onClick={handleShare}
                        disabled={isLoading || isSharing}
                        className="h-[36px] w-[36px] rounded-[10px] bg-muted hover:bg-border shrink-0"
                        aria-label="Partager"
                    >
                        <Share2 className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                    </Button>
                    */}
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col items-center size-full md:max-w-[62.5%] md:min-w-[728px] mx-auto w-full">
                {/* Image / Video Container */}
                <div className="relative w-full h-[400px] bg-foreground-dark shrink-0">
                    {isLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                            <Skeleton className="w-full h-full" />
                        </div>
                    ) : isVideoMedia ? (
                        <video
                            src={getVideoStreamUrl(pictureId)}
                            controls
                            playsInline
                            preload="metadata"
                            poster={previewUrl || undefined}
                            onLoadedMetadata={(e) => {
                                const v = e.currentTarget;
                                if (v.videoWidth && v.videoHeight) {
                                    setImageDimensions({ width: v.videoWidth, height: v.videoHeight });
                                }
                            }}
                            className="w-full h-full object-contain bg-black"
                        />
                    ) : previewUrl ? (
                        <img
                            src={previewUrl}
                            alt={docName}
                            className="w-full h-full object-contain cursor-pointer"
                            onClick={handleImageClick}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-background/60">
                            {t('folders:apercuIndisponible')}
                        </div>
                    )}

                    {/* Top Overlay */}
                    <div className="absolute top-0 left-0 right-0 bg-foreground-dark/60 backdrop-blur-sm px-[20px] pt-[12px] pb-[12px]">
                        <p className="text-[11px] text-background/90 mb-[2px]">
                            {t('folders:horodateEidasLe')} {horodatageDate} à {horodatageTime}
                        </p>
                        <p className="text-[11px] text-background/90">
                            {t('folders:lat')} {formatNumber(picture?.latitude)} / Lon. : {formatNumber(picture?.longitude)}
                        </p>
                    </div>

                    {/* Bottom Overlay with Certificate Link */}
                    {certificate && (
                        <div className="absolute bottom-0 left-0 right-0 bg-foreground-dark/60 backdrop-blur-sm px-[20px] pt-[12px] pb-[12px]">
                            <p className="text-[10px] text-background/80 mb-[2px]">
                                {t('folders:certificationDisponibleEnLigneSur')}
                            </p>
                            <p className="text-[10px] text-secondary">
                                {certificateDisplayUrl}
                            </p>
                        </div>
                    )}

                    {/* Download Certificate Button */}
                    {certificateId && (
                        <div className="absolute bottom-[20px] left-[21px] right-[21px] md:left-auto flex">
                            <Button
                                variant="soft"
                                color="secondary"
                                onClick={handleDownloadCertificate}
                                disabled={isLoading || isDownloadingCertificate}
                                className="bg-secondary/90 backdrop-blur-sm hover:bg-secondary rounded-[12px] px-[16px] py-[12px] h-auto flex items-center justify-center gap-[8px] shadow-lg flex-1 md:flex-none text-foreground"
                                title={t('folders:telechargerLeCertificat')}
                            >
                                {isDownloadingCertificate ? (
                                    <div className="h-5 w-5 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
                                ) : (
                                    <Award className="h-5 w-5" />
                                )}
                                <span className="text-[14px]">
                                    {isDownloadingCertificate ? t('folders:telechargement') : t('folders:telechargerCertificat')}
                                </span>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Details Section */}
                <div className="flex-1 overflow-y-auto w-full px-[21px] pt-[20px] md:pb-[80px]">
                    <div className="md:grid md:grid-cols-2 md:gap-[20px]">
                        {/* Caractéristiques */}
                        <div>
                            <h3 className="text-[14px] text-muted-foreground mb-[12px]">{t('folders:caracteristiques')}</h3>
                            <div className="bg-card rounded-[16px] p-[20px] mb-[20px]">
                                {isLoading ? (
                                    <div className="space-y-[12px]">
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-4 w-2/3" />
                                    </div>
                                ) : (
                                    <div className="space-y-[12px]">
                                        <div className="flex justify-between">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:nomDuFichier')}</span>
                                            <span className="text-[14px] text-foreground">
                                                {picture?.file_name || '—'}
                                            </span>
                                        </div>
                                        <div className="w-full h-[1px] bg-border" />
                                        <div className="flex justify-between">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:format')}</span>
                                            <span className="text-[14px] text-foreground">
                                                {picture?.mime?.split('/')[1]?.toUpperCase() || '—'}
                                            </span>
                                        </div>
                                        <div className="w-full h-[1px] bg-border" />
                                        <div className="flex justify-between">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:poids')}</span>
                                            <span className="text-[14px] text-foreground">
                                                {formatFileSize(picture?.size)}
                                            </span>
                                        </div>
                                        <div className="w-full h-[1px] bg-border" />
                                        <div className="flex justify-between">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:dimensions')}</span>
                                            <span className="text-[14px] text-foreground">
                                                {formatDimensions(imageDimensions)}
                                            </span>
                                        </div>
                                        {isVideoMedia && (
                                            <>
                                                <div className="w-full h-[1px] bg-border" />
                                                <div className="flex justify-between">
                                                    <span className="text-[14px] text-muted-foreground">{t('folders:duree')}</span>
                                                    <span className="text-[14px] text-foreground">
                                                        {formatDuration(picture?.duration_ms)}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                        {!isVideoMedia && (
                                            <>
                                                <div className="w-full h-[1px] bg-border" />
                                                <div className="flex justify-between">
                                                    <span className="text-[14px] text-muted-foreground">{t('folders:qualite')}</span>
                                                    <span className="text-[14px] text-foreground">{getQuality()}</span>
                                                </div>
                                                <div className="w-full h-[1px] bg-border" />
                                                <div className="flex justify-between">
                                                    <span className="text-[14px] text-muted-foreground">{t('folders:orientation')}</span>
                                                    <span className="text-[14px] text-foreground">{getOrientation()}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Horodatage et position GPS */}
                        <div>
                            <h3 className="text-[14px] text-muted-foreground mb-[12px]">{t('folders:horodatageEtPositionGps')}</h3>
                            <div className="bg-card rounded-[16px] p-[20px] mb-[20px]">
                                {isLoading ? (
                                    <div className="space-y-[12px]">
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-3/4" />
                                    </div>
                                ) : (
                                    <div className="space-y-[12px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:dateEtHeure')}</span>
                                            <span className="text-[14px] text-foreground">
                                                {formatDateTime(picture?.created_at)}
                                            </span>
                                        </div>
                                        <div className="w-full h-[1px] bg-border" />
                                        <div className="flex justify-between items-start">
                                            <span className="text-[14px] text-muted-foreground">{t('folders:geolocalisation')}</span>
                                            <div className="text-right">
                                                <p className="text-[14px] text-foreground">
                                                    {t('folders:lat2')} {formatNumber(picture?.latitude)}
                                                </p>
                                                <p className="text-[14px] text-foreground">
                                                    {t('folders:lon')} {formatNumber(picture?.longitude)}
                                                </p>
                                                {picture?.latitude && picture?.longitude && (
                                                    <a
                                                        href={`https://www.google.com/maps?q=${picture.latitude},${picture.longitude}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[12px] text-secondary mt-[4px] underline flex items-center gap-[4px] justify-end"
                                                    >
                                                        <MapPin className="h-3 w-3" />
                                                        {t('folders:voirSurGoogleMaps')}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
